import type { Node, Edge } from "@xyflow/react";
import { NodeData } from "../types/types";
import { nanoid } from "nanoid";
import { convertInlineMarkdownToHtml } from "./textFormatting";

export interface ParsedTextStructure {
  conversationalResponse: string;
  nodes: Node<NodeData>[];
  edges: Edge[];
  parentNodeId: string | null;
  isValid: boolean;
  errors: string[];
}

interface OutlineNode {
  id: string;
  label: string;
  level: number;
  parentId: string | null;
  children: OutlineNode[];
  notes: string; // Body text for this node
}

/**
 * Parse text/markdown structure into FocusMap nodes
 * Supports:
 * - Bullet lists (- or *)
 * - Numbered lists (1. 2. 3.)
 * - Markdown headers (# ## ###)
 * - Indented outlines (2 or 4 spaces)
 */
export function parseTextStructure(
  text: string,
  existingNodes: Node<NodeData>[] = [],
  edges: Edge[] = [],
  selectedNodeId: string | null = null,
): ParsedTextStructure {
  const result: ParsedTextStructure = {
    conversationalResponse: "",
    nodes: [],
    edges: [],
    parentNodeId: selectedNodeId,
    isValid: true,
    errors: [],
  };

  try {
    console.log("=== parseTextStructure ===");
    console.log("Input text:", text);
    console.log("Selected node ID:", selectedNodeId);

    // If no node is selected, find the root/center node
    let effectiveParentId = selectedNodeId;
    if (!effectiveParentId) {
      const rootNode = existingNodes.find((n) => n.type === "center");
      effectiveParentId = rootNode?.id || null;
      console.log("No selection, using root node:", effectiveParentId);
    }

    // Split into lines
    const lines = text.split("\n");
    const outlineNodes: OutlineNode[] = [];
    const nodeStack: OutlineNode[] = [];
    let conversationalLines: string[] = [];
    let foundStructure = false;
    let currentBodyLines: string[] = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmedLine = line.trim();

      // Skip empty lines but keep them for body text formatting
      if (!trimmedLine) {
        if (foundStructure && nodeStack.length > 0) {
          currentBodyLines.push(""); // Preserve paragraph breaks
        }
        continue;
      }

      // Detect structure patterns
      const bulletMatch = line.match(/^(\s*)([-*])\s+(.+)$/);
      const numberedMatch = line.match(/^(\s*)(\d+)\.\s+(.+)$/);
      const headerMatch = line.match(/^(#{1,6})\s+(.+)$/);

      if (bulletMatch || numberedMatch || headerMatch) {
        // Save body text to previous node if any
        if (nodeStack.length > 0 && currentBodyLines.length > 0) {
          const previousNode = nodeStack[nodeStack.length - 1];
          previousNode.notes = currentBodyLines.join("\n").trim();
          currentBodyLines = [];
        }

        foundStructure = true;

        let indent = 0;
        let label = "";
        let level = 0;

        if (bulletMatch) {
          // Bullet list: - Item or * Item
          indent = bulletMatch[1].length;
          label = bulletMatch[3].trim();
          level = Math.floor(indent / 2); // 2 or 4 spaces per level
        } else if (numberedMatch) {
          // Numbered list: 1. Item - preserve the number in the label
          indent = numberedMatch[1].length;
          label = `${numberedMatch[2]}. ${numberedMatch[3].trim()}`;
          level = Math.floor(indent / 2);
        } else if (headerMatch) {
          // Markdown headers: # Title or # 1. Title (preserve numbers)
          const hashes = headerMatch[1].length;
          label = headerMatch[2].trim(); // Keep the full text including any leading numbers
          level = hashes - 1; // # = level 0, ## = level 1, etc.
        }

        // Create outline node
        const outlineNode: OutlineNode = {
          id: nanoid(),
          label,
          level,
          parentId: null,
          children: [],
          notes: "",
        };

        // Determine parent based on level
        while (
          nodeStack.length > 0 &&
          nodeStack[nodeStack.length - 1].level >= level
        ) {
          nodeStack.pop();
        }

        if (nodeStack.length > 0) {
          const parent = nodeStack[nodeStack.length - 1];
          outlineNode.parentId = parent.id;
          parent.children.push(outlineNode);
        } else {
          // Top-level node
          outlineNodes.push(outlineNode);
        }

        nodeStack.push(outlineNode);
      } else {
        // Non-structure line
        if (!foundStructure) {
          // Before any structure - conversational text
          conversationalLines.push(trimmedLine);
        } else if (nodeStack.length > 0) {
          // After structure started - body text for current node
          currentBodyLines.push(trimmedLine);
        }
      }
    }

    // Save body text to last node if any
    if (nodeStack.length > 0 && currentBodyLines.length > 0) {
      const lastNode = nodeStack[nodeStack.length - 1];
      lastNode.notes = currentBodyLines.join("\n").trim();
    }

    // Set conversational response
    result.conversationalResponse = conversationalLines.join(" ").trim();

    // If no structure found, return empty
    if (outlineNodes.length === 0) {
      console.log("No structure found in text");
      result.conversationalResponse = text.trim();
      return result;
    }

    // Convert outline to FocusMap nodes
    const { nodes, edges } = convertOutlineToNodes(
      outlineNodes,
      effectiveParentId,
      existingNodes,
    );

    result.nodes = nodes;
    result.edges = edges;
    result.parentNodeId = effectiveParentId;

    console.log(
      `✅ Parsed ${result.nodes.length} nodes and ${result.edges.length} edges from text`,
    );
  } catch (error) {
    console.error("Error parsing text structure:", error);
    result.errors.push(
      `Parsing error: ${error instanceof Error ? error.message : String(error)}`,
    );
    result.isValid = false;
  }

  return result;
}

/**
 * Convert outline nodes to FocusMap nodes and edges
 */
function convertOutlineToNodes(
  outlineNodes: OutlineNode[],
  parentNodeId: string | null,
  existingNodes: Node<NodeData>[],
): { nodes: Node<NodeData>[]; edges: Edge[] } {
  const nodes: Node<NodeData>[] = [];
  const edges: Edge[] = [];

  function processNode(
    outlineNode: OutlineNode,
    actualParentId: string | null,
  ) {
    // Convert markdown in label to HTML
    const htmlLabel = convertInlineMarkdownToHtml(outlineNode.label);

    // Convert markdown notes to HTML
    let htmlNotes = "";
    if (outlineNode.notes) {
      // Convert inline markdown (bold, italic)
      htmlNotes = convertInlineMarkdownToHtml(outlineNode.notes);
      // Convert line breaks to <br>
      htmlNotes = htmlNotes.replace(/\n/g, "<br>");
      // Wrap in paragraph if not empty
      if (htmlNotes.trim()) {
        htmlNotes = `<p>${htmlNotes}</p>`;
      }
    }

    if (htmlLabel !== outlineNode.label) {
      console.log(
        "Converted markdown label:",
        outlineNode.label,
        "->",
        htmlLabel,
      );
    }

    if (htmlNotes) {
      console.log(
        "Added notes to node:",
        outlineNode.label,
        "->",
        htmlNotes.substring(0, 100) + "...",
      );
    }

    // Create FocusMap node
    const node: Node<NodeData> = {
      id: outlineNode.id,
      type: "mindmap",
      position: { x: 0, y: 0 }, // Will be calculated later
      data: {
        label: htmlLabel,
        type: null,
        notes: htmlNotes,
        icon: "",
        complete: false,
        flagged: "none",
        links: [],
        priority: "",
        value: "",
        values: [],
        rollupValues: [],
        presentationOrder: "",
        backgroundColor: "#ffffff",
        color: "#000000",
        status: "none",
        customProperties: [],
        object: "",
        objectType: "",
        objectWidth: 0,
        objectHeight: 0,
        event: {} as any,
        address: {} as any,
        task: {} as any,
        ms365Document: {} as any,
      },
      draggable: true,
      selectable: true,
    };

    // Set parent relationship
    if (actualParentId) {
      (node as any).parentId = actualParentId;

      // Create edge
      edges.push({
        id: `${actualParentId}-${outlineNode.id}`,
        source: actualParentId,
        target: outlineNode.id,
        type: "mindmap",
      });
    }

    nodes.push(node);

    // Process children
    for (const child of outlineNode.children) {
      processNode(child, outlineNode.id);
    }
  }

  // Process all top-level nodes
  for (const outlineNode of outlineNodes) {
    processNode(outlineNode, parentNodeId);
  }

  return { nodes, edges };
}

/**
 * Parse simple indented outline (no bullets or numbers)
 * Useful for copy/paste from plain text
 */
export function parseIndentedOutline(
  text: string,
  selectedNodeId: string | null = null,
): ParsedTextStructure {
  const result: ParsedTextStructure = {
    conversationalResponse: "",
    nodes: [],
    edges: [],
    parentNodeId: selectedNodeId,
    isValid: true,
    errors: [],
  };

  try {
    const lines = text.split("\n").filter((line) => line.trim());
    if (lines.length === 0) {
      return result;
    }

    const outlineNodes: OutlineNode[] = [];
    const nodeStack: OutlineNode[] = [];

    for (const line of lines) {
      // Calculate indent level
      const indent = line.search(/\S/);
      const label = line.trim();
      const level = Math.floor(indent / 2);

      // Create outline node
      const outlineNode: OutlineNode = {
        id: nanoid(),
        label,
        level,
        parentId: null,
        children: [],
        notes: "",
      };

      // Determine parent
      while (
        nodeStack.length > 0 &&
        nodeStack[nodeStack.length - 1].level >= level
      ) {
        nodeStack.pop();
      }

      if (nodeStack.length > 0) {
        const parent = nodeStack[nodeStack.length - 1];
        outlineNode.parentId = parent.id;
        parent.children.push(outlineNode);
      } else {
        outlineNodes.push(outlineNode);
      }

      nodeStack.push(outlineNode);
    }

    // Convert to nodes
    const { nodes, edges } = convertOutlineToNodes(
      outlineNodes,
      selectedNodeId,
      [],
    );

    result.nodes = nodes;
    result.edges = edges;
  } catch (error) {
    console.error("Error parsing indented outline:", error);
    result.errors.push(
      `Parsing error: ${error instanceof Error ? error.message : String(error)}`,
    );
    result.isValid = false;
  }

  return result;
}
